// src/pages/public/Home.js — GATEON 메인 (C안 · 토스풍 클린, 2026-11-03 사용자 선택)
// 흰 바탕 + 아주 굵은 고딕 제목 + 가운데 휴대폰 티켓과 떠 있는 알림 카드, 기능은 좌우 교차 소개.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Check } from 'lucide-react';
import PublicHeader from '../../components/common/PublicHeader';
import PublicFooter from '../../components/common/PublicFooter';
import Seo, { SITE_URL } from '../../components/common/Seo';
import { eventService } from '../../services/eventService';
import api from '../../services/api';
import { COMPANY } from '../../config/company';
import './Home.scss';

const HOME_DESCRIPTION =
  '전시·축제·투어·컨퍼런스 예매 페이지를 직접 만들고, 결제부터 현장 QR 입장과 매출 확인까지 한곳에서 처리하는 행사 운영 플랫폼입니다. 구축비 없이 바로 시작하세요.';

// ── 움직임 (GSAP · Three.js) — 별도 청크로 지연 로드 ─────────────
// 사전 렌더링(prerender.js 가 __GATEON_PRERENDER__ 표시)·'동작 줄이기' 설정에서는 아무것도 하지 않는다.
const motionAllowed = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && !window.__GATEON_PRERENDER__
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// 3D 배경은 넓은 화면·마우스 환경에서만 (휴대폰·데이터 절약 모드·저사양은 건너뜀)
const can3D = () => {
  if (!window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches) return false;
  if ((navigator.hardwareConcurrency || 4) < 4 || navigator.connection?.saveData) return false;
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') || c.getContext('webgl'));
  } catch (e) {
    return false;
  }
};

// 사전 렌더링 HTML 로 이미 첫 화면을 보여 줬다면, React 가 다시 그릴 때 카드 등장 효과를 한 번 더 틀지 않는다
let heroIntroShown = false;

const KST = 'Asia/Seoul';
const todayKst = () => new Intl.DateTimeFormat('en-CA', { timeZone: KST }).format(new Date());
const fmtDay = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-').map(Number);
  return `${m}월 ${d}일`;
};

// ── 히어로: 휴대폰 티켓 + 떠 있는 카드 (장식, 화면낭독기에서는 숨김) ──
const HeroScene = ({ event }) => (
  <div className="hero-scene" aria-hidden="true">
    <div className="hero-phone">
      <div className="hero-phone__screen">
        <div
          className="hero-phone__poster"
          style={{ backgroundImage: `url(${event?.thumbnail_url || '/img/events/monet-2024.webp'})` }}
        />
        <div className="hero-phone__body">
          <strong>{event?.name || '모네: 빛을 그리다'}</strong>
          <small>방문일 {fmtDay(event?.start_date > todayKst() ? event.start_date : todayKst())}</small>
          <div className="hero-phone__qr">
            <QRCodeSVG value={`${SITE_URL}/demo`} size={132} level="M" fgColor="#191F28" />
            <i className="hero-phone__scan" />
            <span className="hero-phone__stamp">입장 완료</span>
          </div>
          <span className="hero-phone__hint">입구에서 이 QR을 보여 주세요</span>
          <dl className="hero-phone__info">
            <div><dt>입장 시간</dt><dd>14:00 ~ 15:00</dd></div>
            <div><dt>권종</dt><dd>성인 2매</dd></div>
          </dl>
        </div>
      </div>
    </div>
    <div className="float-card float-card--pay" data-depth="0.8"><span className="float-card__ic is-blue">₩</span><div><b>결제 완료</b><small>30,000원 · 토스페이먼츠</small></div></div>
    <div className="float-card float-card--entry" data-depth="1.3"><span className="float-card__ic is-green">✓</span><div><b>입장 완료</b><small>성인 입장권 2매</small></div></div>
    <div className="float-card float-card--new" data-depth="1"><span className="float-card__ic is-orange js-new-badge">+3</span><div><b>새 예약 <span className="js-new-count">3</span>건</b><small>방금 들어온 예약</small></div></div>
    <div className="float-card float-card--week" data-depth="1.5">
      <div><b>이번 주 예약</b><small>토요일이 가장 많아요</small></div>
      <div className="float-card__bars">{[35, 45, 50, 58, 75, 100, 88].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
    </div>
  </div>
);

// ── 기능 소개 (좌우 교차) ─────────────────────────────
const PayMock = () => (
  <div className="mock mock--pay" aria-hidden="true">
    <div className="mock__row"><span>성인 입장권 × 2</span><b>30,000원</b></div>
    <div className="mock__row is-coupon"><span>오픈 기념 쿠폰</span><b>-3,000원</b></div>
    <div className="mock__total"><span>결제 금액</span><b>27,000원</b></div>
    <div className="mock__btn">27,000원 결제하기</div>
    <div className="mock__toast"><span className="float-card__ic is-blue">₩</span><div><b>결제 완료</b><small>QR 티켓이 발급됐습니다</small></div></div>
  </div>
);

const DashMock = () => (
  <div className="mock mock--dash" aria-hidden="true">
    <div className="mock__kpis">
      <div><small>오늘 매출</small><b>1,240,000원</b></div>
      <div><small>오늘 예약</small><b>86건</b></div>
    </div>
    <small className="mock__label">일별 예약</small>
    <div className="mock__bars">
      {[['월', 34], ['화', 42], ['수', 48], ['목', 55], ['금', 72], ['토', 96], ['일', 88]].map(([d, h]) => (
        <span key={d}><i style={{ height: `${h}%` }} /><small>{d}</small></span>
      ))}
    </div>
  </div>
);

const FEATURES = [
  {
    key: 'page',
    kicker: '예매 페이지',
    title: ['행사 정보만 넣으면', '판매가 시작됩니다'],
    body: '포스터와 소개, 일정, 오시는 길을 넣으면 gateon.kr/e/행사주소로 예매 페이지가 바로 열립니다. 권종과 가격, 판매 수량도 같은 화면에서 정합니다.',
    visual: <img src="/img/home/feature-page.webp" alt="" width="1600" height="900" loading="lazy" decoding="async" />,
  },
  {
    key: 'pay',
    kicker: '결제',
    title: ['결제는 토스페이먼츠로,', '티켓은 결제한 뒤에만'],
    body: '카드와 간편결제로 받고, 결제를 마친 주문에만 입장용 QR이 나옵니다. 할인 쿠폰과 협력사 초대권도 직접 발급할 수 있습니다.',
    visual: <PayMock />,
  },
  {
    key: 'entry',
    kicker: '현장 입장',
    title: ['입장은', 'QR 한 번으로 끝'],
    body: '직원 휴대폰으로 티켓 QR을 찍으면 결제 여부와 방문일, 중복 입장을 확인해 바로 입장 처리합니다. 입장 시간대를 정해 혼잡을 나눌 수도 있습니다.',
    visual: <img src="/img/home/feature-entry.webp" alt="" width="1600" height="900" loading="lazy" decoding="async" />,
  },
  {
    key: 'report',
    kicker: '매출 확인',
    title: ['매출과 예약을', '한눈에'],
    body: '매출 추이와 일별 예약, 권종별 판매 비중을 대시보드에서 보고, 주문·입장 내역은 엑셀로 내려받습니다.',
    visual: <DashMock />,
  },
];

const STEPS = [
  { title: '무료 가입', body: '회사 정보와 운영자 계정만 입력하면 바로 관리 화면이 열립니다.' },
  { title: '행사 등록', body: '권종과 가격, 판매 수량을 정하고 예매 페이지를 엽니다.' },
  { title: '판매와 입장', body: '결제는 토스페이먼츠로, 입장은 직원 휴대폰 QR 스캔으로 처리합니다.' },
];

const PLANS = [
  { name: 'Basic', price: '월 0원부터', fit: '한 번 열리는 소규모 행사', items: ['행사 1건', '온라인 결제와 QR 입장', '예약 안내 메일'], cta: { label: '무료로 시작하기', to: '/business/signup' } },
  { name: 'Pro', price: '맞춤 견적', fit: '정기 행사나 규모가 큰 행사', items: ['행사 수 제한 없음', '할인 쿠폰과 협력사 초대권', '관리자 여러 명과 권한 구분', '기술 지원 우선 처리'], cta: { label: 'Pro 상담 신청', to: '/contact?plan=pro' }, featured: true },
  { name: 'Enterprise', price: '별도 문의', fit: '전용 환경이 필요한 기관', items: ['기관별 데이터 분리', '전용 도메인과 SLA', '전담 매니저 배정'], cta: { label: 'Enterprise 상담 신청', to: '/contact?plan=enterprise' } },
];

// ── 지금 예매 중인 행사 (실제 데이터 — 없으면 숨김) ─────
const OpenEvents = ({ events }) => {
  if (!events.length) return null;
  return (
    <section className="home-events" aria-labelledby="events-title">
      <div className="gt-container">
        <div className="home-head home-head--row">
          <div>
            <p className="home-kicker">지금 예매 중</p>
            <h2 id="events-title">게이트온에서 판매 중인 행사</h2>
          </div>
          <Link to="/demo" className="home-more">행사 전체 보기</Link>
        </div>
        <ul className="home-events__list">
          {events.map((ev) => (
            <li key={ev.id}>
              <Link to={`/e/${ev.slug}`} className="event-card">
                <span className="event-card__img">
                  {ev.thumbnail_url && <img src={ev.thumbnail_url} alt="" loading="lazy" decoding="async" />}
                </span>
                <strong>{ev.name}</strong>
                <small>{[ev.location, ev.end_date && `${fmtDay(ev.end_date)}까지`].filter(Boolean).join(' · ')}</small>
                <span className="event-card__cta">예매하기</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// ── 최신 블로그 (글이 없으면 숨김) ─────────────────────
const LatestPosts = () => {
  const { data } = useQuery({
    queryKey: ['home-latest-posts'],
    queryFn: async () => (await api.get('/blog', { params: { page: 1, limit: 3 } })).data,
    retry: false,
    staleTime: 300_000,
  });
  const posts = (Array.isArray(data) ? data : data?.items || []).slice(0, 3);
  if (!posts.length) return null;
  return (
    <section className="home-blog" aria-labelledby="blog-title">
      <div className="gt-container">
        <div className="home-head home-head--row">
          <div>
            <p className="home-kicker">블로그</p>
            <h2 id="blog-title">행사 운영 가이드</h2>
          </div>
          <a href="/blog" className="home-more">블로그 글 전체 보기</a>
        </div>
        <ul className="home-blog__list">
          {posts.map((p) => (
            <li key={p.slug}>
              <a href={`/blog/${p.slug}`} className="post-card">
                <span className="post-card__img">
                  {p.cover_image_url && <img src={p.cover_image_url} alt="" loading="lazy" decoding="async" />}
                </span>
                {p.category && <small>{p.category}</small>}
                <strong>{p.title}</strong>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// ── 페이지 ──────────────────────────────────────────
const Home = () => {
  const location = useLocation();
  const today = todayKst();
  const rootRef = useRef(null);
  const tickets3dRef = useRef(null);
  const [staticIntro] = useState(
    () => !heroIntroShown && typeof document !== 'undefined' && document.documentElement.dataset.prerendered === 'true',
  );
  useEffect(() => { heroIntroShown = true; }, []);

  // 인터랙션: GSAP 장면은 첫 화면이 그려진 뒤 한가할 때, 3D 티켓은 방문자가 움직이기 시작할 때 로드
  useEffect(() => {
    if (!motionAllowed()) return undefined;
    let disposed = false;
    let stopMotion = null;
    let scene3d = null;
    let cancel3D = null;

    const idle = (fn, timeout) => {
      if (window.requestIdleCallback) {
        const id = window.requestIdleCallback(fn, { timeout });
        return () => window.cancelIdleCallback(id);
      }
      const id = window.setTimeout(fn, 300);
      return () => window.clearTimeout(id);
    };
    const cancelMotion = idle(() => {
      import(/* webpackChunkName: "home-motion" */ './homeMotion')
        .then(({ initHomeMotion }) => {
          if (disposed || !rootRef.current) return;
          stopMotion = initHomeMotion(rootRef.current, {
            onPointer: (x, y) => scene3d && scene3d.setPointer(x, y),
            onScroll: (p) => scene3d && scene3d.setScroll(p),
          });
        })
        .catch(() => {});
    }, 1200);

    // 3D 는 방문자가 마우스를 움직이거나 스크롤을 시작할 때 불러온다 (첫 화면 로딩과 겹치지 않게)
    const wake = ['pointermove', 'wheel', 'keydown', 'scroll'];
    const unwake = () => wake.forEach((ev) => window.removeEventListener(ev, onWake));
    function onWake() {
      unwake();
      cancel3D = idle(load3D, 800);
    }
    function load3D() {
      cancel3D = null;
      import(/* webpackChunkName: "home-3d" */ './heroTickets3d')
        .then(({ mountHeroTickets }) => {
          if (disposed || !tickets3dRef.current || !rootRef.current) return;
          const hero = rootRef.current;
          scene3d = mountHeroTickets(tickets3dRef.current, {
            avoid: () => Array.from(hero.querySelectorAll(
              '.home-hero__inner > .home-kicker, .home-hero__title, .home-hero__lead, .home-hero__actions, .hero-phone, .float-card',
            )),
          });
        })
        .catch(() => {});
    }
    if (can3D()) wake.forEach((ev) => window.addEventListener(ev, onWake, { passive: true }));

    return () => {
      disposed = true;
      unwake();
      cancelMotion();
      if (cancel3D) cancel3D();
      if (stopMotion) stopMotion();
      if (scene3d) scene3d.destroy();
    };
  }, []);

  const { data: eventData } = useQuery({
    queryKey: ['home-open-events'],
    queryFn: () => eventService.getActiveEvents(),
    staleTime: 60_000,
  });
  const events = useMemo(
    () => (Array.isArray(eventData) ? eventData : [])
      .filter((e) => !e.end_date || e.end_date >= today)
      .slice(0, 3),
    [eventData, today],
  );

  // /#features, /#pricing 으로 들어오면 해당 구역으로 이동
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }));
  }, [location.hash]);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'GATEON',
      alternateName: '게이트온',
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
      slogan: '예매부터 입장까지, 게이트온',
      description: HOME_DESCRIPTION,
      contactPoint: {
        '@type': 'ContactPoint',
        email: COMPANY.email,
        telephone: '+82-2-325-4820',
        contactType: 'sales',
        areaServed: 'KR',
        availableLanguage: 'Korean',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: '게이트온 GATEON',
      alternateName: ['GATEON', '게이트온'],
      url: `${SITE_URL}/`,
      inLanguage: 'ko-KR',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ];

  return (
    <div className="home" ref={rootRef}>
      <Seo title="게이트온 GATEON | 행사 예매·QR 입장 관리 플랫폼" description={HOME_DESCRIPTION} path="/" jsonLd={jsonLd} />
      <Helmet>
        <link rel="alternate" type="application/rss+xml" title="게이트온 블로그" href={`${SITE_URL}/blog/rss.xml`} />
        {/* 검색 등록 소유 확인 — frontend/.env.production 값이 있으면 사전 렌더링 HTML 에 포함 */}
        {process.env.REACT_APP_NAVER_SITE_VERIFICATION && (
          <meta name="naver-site-verification" content={process.env.REACT_APP_NAVER_SITE_VERIFICATION} />
        )}
        {process.env.REACT_APP_GOOGLE_SITE_VERIFICATION && (
          <meta name="google-site-verification" content={process.env.REACT_APP_GOOGLE_SITE_VERIFICATION} />
        )}
      </Helmet>
      <PublicHeader />

      <main id="main">
        {/* 1) 히어로 */}
        <section className={`home-hero${staticIntro ? ' home-hero--static' : ''}`}>
          <div className="home-hero__blob" aria-hidden="true" />
          <div className="home-hero__3d" ref={tickets3dRef} aria-hidden="true" />
          <div className="gt-container home-hero__inner">
            <p className="home-kicker">행사 운영 플랫폼, 게이트온</p>
            <h1 className="home-hero__title">
              <span>티켓 판매부터 입장까지</span> <span>게이트온 하나로</span>
            </h1>
            <p className="home-hero__lead">
              예매 페이지는 10분 만에 열고, 입장은 QR 한 번으로.<br className="br-desktop" /> 결제와 매출 확인까지 한 화면에서 끝내세요.
            </p>
            <div className="home-hero__actions">
              <Link to="/business/signup" className="gt-btn gt-btn--primary gt-btn--lg">무료로 시작하기</Link>
              <Link to="/demo" className="gt-btn gt-btn--sub gt-btn--lg">데모 보기</Link>
            </div>
            <HeroScene event={events[0]} />
          </div>
        </section>

        {/* 2) 기능 — 좌우 교차 */}
        <section id="features" className="home-features" aria-label="주요 기능">
          {FEATURES.map((f, i) => (
            <div key={f.key} className={`feature-row${i % 2 ? ' feature-row--reverse' : ''}`}>
              <div className="gt-container feature-row__inner">
                <div className="feature-row__text">
                  <p className="home-kicker">{f.kicker}</p>
                  <h2>{f.title[0]}<br />{f.title[1]}</h2>
                  <p>{f.body}</p>
                </div>
                <div className="feature-row__visual">{f.visual}</div>
              </div>
            </div>
          ))}
        </section>

        {/* 3) 지금 예매 중인 행사 */}
        <OpenEvents events={events} />

        {/* 4) 시작 3단계 */}
        <section className="home-steps" aria-labelledby="steps-title">
          <div className="gt-container">
            <div className="home-head home-head--center">
              <p className="home-kicker">시작하기</p>
              <h2 id="steps-title">시작은 세 단계면 충분합니다</h2>
            </div>
            <ol className="home-steps__list">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="home-steps__bar" aria-hidden="true"><i /></span>
                  <b aria-hidden="true">{i + 1}</b>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 5) 요금 */}
        <section id="pricing" className="home-pricing" aria-labelledby="pricing-title">
          <div className="gt-container">
            <div className="home-head home-head--center">
              <p className="home-kicker">요금</p>
              <h2 id="pricing-title">구축비 없이 시작하세요</h2>
              <p>정확한 비용은 행사 규모와 필요한 기능을 듣고 안내해 드립니다.</p>
            </div>
            <div className="plans">
              {PLANS.map((p) => (
                <div key={p.name} className={`plan${p.featured ? ' plan--featured' : ''}`}>
                  {p.featured && <span className="plan__badge">추천</span>}
                  <h3>{p.name}</h3>
                  <p className="plan__price">{p.price}</p>
                  <p className="plan__fit">{p.fit}</p>
                  <ul>
                    {p.items.map((it) => (
                      <li key={it}><Check size={18} strokeWidth={2.6} aria-hidden="true" />{it}</li>
                    ))}
                  </ul>
                  {p.cta.to ? (
                    <Link to={p.cta.to} className={`gt-btn ${p.featured ? 'gt-btn--primary' : 'gt-btn--sub'}`}>{p.cta.label}</Link>
                  ) : (
                    <a href={p.cta.href} className={`gt-btn ${p.featured ? 'gt-btn--primary' : 'gt-btn--sub'}`}>{p.cta.label}</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6) 블로그 */}
        <LatestPosts />

        {/* 7) 마무리 */}
        <section className="home-closing" aria-labelledby="closing-title">
          <div className="gt-container">
            <div className="home-closing__panel">
              <h2 id="closing-title">행사 정보만 있으면<br />오늘 예매를 열 수 있습니다</h2>
              <p>전화(<a href={`tel:${COMPANY.phone}`}>{COMPANY.phone}</a>)나 이메일(<a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>)로도 문의할 수 있습니다.</p>
              <div className="home-closing__actions">
                <Link to="/business/signup" className="gt-btn gt-btn--primary gt-btn--lg">무료로 시작하기</Link>
                <Link to="/contact" className="gt-btn gt-btn--on-dark gt-btn--lg">도입 상담 신청</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Home;
